import moment from 'moment';
import React, { FunctionComponent, useMemo } from 'react';
import { GoDotFill } from 'react-icons/go';
import { Step4Props } from './types';
import { IAgendaItem } from '../../../../../../app/resource/agenda-item/agenda-item.types';
import { IUser } from '../../../../../../app/resource/user/user.types';
import { useSelectWithParams } from '../../../../../../utils/selector.utils';
import { userSelectors } from '../../../../../../app/resource/user/user.selectors';

const Step4: FunctionComponent<Step4Props> = ({ defaultValues, onSubmit }) => {
  const administrators = useSelectWithParams(
    userSelectors.selectResourcesById,
    defaultValues.administrators,
  ) as IUser[];
  const members = useSelectWithParams(userSelectors.selectResourcesById, defaultValues.members) as IUser[];

  const currentDate = useMemo(() => {
    return {
      dayOfWeek: moment(defaultValues.startDate).format('dddd'),
      dayOfMonth: moment(defaultValues.startDate).date(),
      monthAndDay: moment(defaultValues.startDate).format('MMMM Do'),
    };
  }, [defaultValues.startDate]);

  const handleGetOptionLabel = (interval: string, repeatType: string, repeatEvery: number) => {
    switch (interval) {
      case 'weekly':
        return `Weekly on ${currentDate.dayOfWeek}`;
      case 'monthly':
        return `Monthly on the ${currentDate.dayOfMonth}`;
      case 'annually':
        return `Annually on ${currentDate.monthAndDay}`;
      case 'daily':
        if (repeatEvery === 1) {
          return 'Daily';
        }
        return `Every ${repeatEvery} days`;
      default:
        return `Every ${repeatEvery} ${repeatType}`;
    }
  };

  const handleSubmit = (e: any) => {
    e.preventDefault();
    onSubmit();
  };
  return (
    <form id="meeting-templates-form" onSubmit={handleSubmit}>
      <div>
        <div className="row my-1">
          <div className="col-12">
            <div className="field-container m-1">
              <div>
                <span className="bold">Meeting Name:</span>
                <span className="ml-1">{defaultValues.title}</span>
              </div>
            </div>
          </div>
        </div>
        <div className="row my-1">
          <div className="col-12">
            <div className="field-container m-1">
              <div>
                <span className="bold">Meeting URL:</span>
                <span className="ml-1">{defaultValues.sharepointUrl}</span>
              </div>
            </div>
          </div>
        </div>
        <div className="row my-1">
          <div className="col-12">
            <div className="field-container m-1">
              <div>
                <span className="bold">Start Date:</span>
                <span className="ml-1">{moment(defaultValues.startDate).format('YYYY-MM-DD')}</span>
              </div>
            </div>
          </div>
        </div>
        <div className="row my-1">
          <div className="col-12">
            <div className="field-container m-1">
              <div>
                <span className="bold">Start time:</span>
                <span className="ml-1">{moment(defaultValues.startTime, 'H:mm').format('hh:mm A')}</span>
              </div>
            </div>
          </div>
        </div>
        {administrators.length > 0 && (
          <div className="row my-1">
            <div className="col-12">
              <div className="field-container m-1">
                <div>
                  <span className="bold">Administrators:</span>
                  <div className="my-1">
                    <ul>
                      {administrators.map(
                        (user: IUser) =>
                          user && (
                            <li key={user.id} className="align-center flex p-1" style={{ borderBottom: 'none' }}>
                              <div className="flex" style={{ minWidth: 'fit-content' }}>
                                <GoDotFill />
                                <span className="ml-1">
                                  {user.firstName} {user.lastName}
                                </span>
                              </div>
                            </li>
                          ),
                      )}
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
        {members.length > 0 && (
          <div className="row my-1">
            <div className="col-12">
              <div className="field-container m-1">
                <div>
                  <span className="bold">Members:</span>
                  <div className="my-1">
                    <ul>
                      {members.map(
                        (user: IUser) =>
                          user && (
                            <li key={user.id} className="align-center flex p-1" style={{ borderBottom: 'none' }}>
                              <div className="flex" style={{ minWidth: 'fit-content' }}>
                                <GoDotFill />
                                <span className="ml-1">
                                  {user.firstName} {user.lastName}
                                </span>
                              </div>
                            </li>
                          ),
                      )}
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        <div className="row my-1">
          <div className="col-12">
            <div className="field-container m-1">
              <div>
                <span className="bold">Repeat:</span>
                <span className="ml-1">
                  {defaultValues.repeatEvery == 0 ? (
                    <>Do not repeat</>
                  ) : (
                    <>
                      {handleGetOptionLabel(
                        defaultValues.interval,
                        defaultValues.repeatType,
                        defaultValues.repeatEvery,
                      )}
                    </>
                  )}
                </span>
              </div>
            </div>
          </div>
        </div>
        <div className="row">
          <div className="col-12">
            <div className="field-container m-1">
              <div className="bold">Agenda:</div>
              <div className="my-1">
                <ul>
                  {defaultValues.agendaItems.map(
                    (agendaItem: IAgendaItem) =>
                      agendaItem && (
                        <li key={agendaItem.id} className="align-center flex p-1" style={{ borderBottom: 'none' }}>
                          <div className="flex" style={{ minWidth: 'fit-content' }}>
                            <GoDotFill />
                            <span className="ml-1">{agendaItem?.title}</span>
                          </div>
                          <div>
                            <span className="ml-1">({agendaItem?.duration} min)</span>
                          </div>
                        </li>
                      ),
                  )}
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>
    </form>
  );
};
export default Step4;
